/**
 * PersonaDetailDrawer — la scheda di una persona: dati, tag, diario.
 *
 * Tre zone: in alto la reperibilità (il motivo per cui la rubrica esiste), al
 * centro la nota vocale e i tag, in basso il diario delle interazioni in ordine
 * cronologico inverso.
 *
 * Il diario è APPEND-ONLY: nessuna voce si sovrascrive. È ciò che permetterà, il
 * giorno in cui si vorranno i promemoria di ricontatto, di calcolare "ultimo
 * contatto" senza migrare nulla.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, Drawer, IconButton, Link, Paper,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close, Delete, Email, LinkedIn, Phone, Save, WhatsApp,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { TagPicker } from './TagPicker';
import { NotaVocaleCard } from './NotaVocaleCard';
import { DossierWebCard } from './DossierWebCard';
import { CollegamentiCard } from './CollegamentiCard';
import { RevisioneAnalisiDialog, type EsitoRevisione } from './RevisioneAnalisiDialog';
import { TelefoniEditor } from './TelefoniEditor';
import { CANALE_LABELS, formatIsoDate, iniziali } from './labels';
import { numeroPerCollegamento } from '@shared/rete/telefoniRete';
import {
  aggiungiInterazione, eliminaPersona, listInterazioni, updatePersona,
} from '../../services/reteContattiService';
import type {
  AnalizzaNotaVocaleResponse, FaccettaRete, InterazioneRete, PersonaRete,
  PersonaReteWriteInput, TagRete, TelefonoRete,
} from '../../types/reteContatti';

interface Props {
  persona: PersonaRete | null;
  /** Tutte le persone: servono a risolvere i nomi dei collegamenti. */
  tutte: PersonaRete[];
  /** Apre un'altra scheda: si naviga dentro la rete. */
  onApriPersona: (id: string) => void;
  faccette: FaccettaRete[];
  tag: TagRete[];
  onClose: () => void;
  /** Dati cambiati: il chiamante ricarica la lista. */
  onSaved: () => Promise<void>;
  onTassonomiaCambiata: () => Promise<void>;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

/**
 * Campi testuali modificabili in linea, con la loro etichetta. I numeri di telefono non sono qui: sono un elenco
 * (`TelefoniEditor`), e compaiono subito dopo il ruolo.
 */
const CAMPI_TESTO: Array<{ campo: Exclude<keyof PersonaReteWriteInput, 'telefoni' | 'tag'>; label: string; multi?: boolean }> = [
  // Il nome si corregge: un nome sentito al telefono si sbaglia continuamente
  // (Guido/Guidario), ed è il campo che rende la persona ritrovabile.
  { campo: 'nome', label: 'Nome e cognome' },
  { campo: 'azienda_testo', label: 'Azienda' },
  { campo: 'ruolo_testo', label: 'Ruolo' },
  { campo: 'email', label: 'Email' },
  { campo: 'linkedin', label: 'LinkedIn' },
  { campo: 'conosciuto_come', label: 'Come vi siete conosciuti' },
  { campo: 'cosa_cerca', label: 'Cosa cerca lui', multi: true },
  { campo: 'cosa_potrebbe_nascere', label: 'Cosa potrebbe nascere', multi: true },
  { campo: 'note_libere', label: 'Note', multi: true },
];

export const PersonaDetailDrawer: React.FC<Props> = ({
  persona, tutte, onApriPersona, faccette, tag, onClose, onSaved,
  onTassonomiaCambiata, showSnack,
}) => {
  const [bozza, setBozza] = useState<Partial<PersonaReteWriteInput>>({});
  const [tagBozza, setTagBozza] = useState<string[]>([]);
  const [interazioni, setInterazioni] = useState<InterazioneRete[]>([]);
  const [caricandoDiario, setCaricandoDiario] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [analisi, setAnalisi] = useState<
    { dati: AnalizzaNotaVocaleResponse; daVoce: boolean } | null
  >(null);

  const personaId = persona?.id ?? null;

  // Allinea la bozza quando cambia la persona mostrata.
  useEffect(() => {
    setBozza({});
    setTagBozza(persona?.tag ?? []);
    setAnalisi(null);
  }, [personaId, persona?.tag]);

  const caricaDiario = useCallback(async () => {
    if (!personaId) return;
    setCaricandoDiario(true);
    try {
      setInterazioni(await listInterazioni(personaId));
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Diario non caricato', 'error');
    } finally {
      setCaricandoDiario(false);
    }
  }, [personaId, showSnack]);

  useEffect(() => {
    if (personaId) caricaDiario();
    else setInterazioni([]);
  }, [personaId, caricaDiario]);

  if (!persona) return null;

  const valore = (campo: Exclude<keyof PersonaReteWriteInput, 'telefoni' | 'tag'>): string => {
    if (campo in bozza) return String(bozza[campo] ?? '');
    const v = (persona as unknown as Record<string, unknown>)[campo];
    return v == null ? '' : String(v);
  };
  const telefoni: TelefonoRete[] = 'telefoni' in bozza ? bozza.telefoni ?? [] : persona.telefoni ?? [];

  const sporca = Object.keys(bozza).length > 0 ||
    tagBozza.length !== (persona.tag ?? []).length ||
    tagBozza.some((t) => !(persona.tag ?? []).includes(t));

  const salva = async () => {
    // Il nome è l'unico campo obbligatorio: si può correggere, non svuotare.
    if ('nome' in bozza && !String(bozza.nome ?? '').trim()) {
      showSnack('Il nome non può restare vuoto', 'error');
      return;
    }
    setSalvando(true);
    try {
      const patch = { ...bozza, tag: tagBozza };
      if (typeof patch.nome === 'string') patch.nome = patch.nome.trim();
      await updatePersona(persona.id, patch, persona.tag ?? []);
      setBozza({});
      await onSaved();
      showSnack('Scheda aggiornata', 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Salvataggio non riuscito', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const elimina = async () => {
    setSalvando(true);
    try {
      await eliminaPersona(persona);
      onClose();
      await onSaved();
      showSnack('Scheda eliminata', 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Eliminazione non riuscita', 'error');
    } finally {
      setSalvando(false);
    }
  };

  /** Applica l'esito della revisione: interazione + tag + campi accettati. */
  const applicaRevisione = async (esito: EsitoRevisione) => {
    try {
      await aggiungiInterazione(persona.id, {
        quando: esito.quando,
        canale: esito.canale,
        sintesi: esito.nota,
        ...(esito.trascrizioneGrezza ? { trascrizione_grezza: esito.trascrizioneGrezza } : {}),
        tag_agganciati: esito.tagFinali,
        da_nota_vocale: esito.daNotaVocale,
      });
      await updatePersona(
        persona.id,
        { ...esito.patchScheda, tag: esito.tagFinali },
        persona.tag ?? [],
      );
      setTagBozza(esito.tagFinali);
      setAnalisi(null);
      await Promise.all([caricaDiario(), onSaved()]);
      showSnack('Nota salvata', 'success');
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Salvataggio nota non riuscito', 'error');
    }
  };

  // I bottoni in cima usano il numero principale, cioè il primo.
  const telefonoPulito = numeroPerCollegamento(telefoni[0]?.numero ?? '');

  return (
    <>
      <Drawer
        anchor="right"
        open={!!persona}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 560 }, bgcolor: tokens.color.paper },
        }}
        // zIndex sopra l'AppBar (stesso accorgimento di LeadDetailDrawer).
        sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
      >
        <Box sx={{ p: 3 }}>
          {/* HEADER ------------------------------------------------------- */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
            <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
              {/* Stesso monogramma della lista: la scheda che si apre è
                  riconoscibilmente la riga su cui si è cliccato. */}
              <Box
                sx={{
                  width: 44, height: 44, borderRadius: 2, flexShrink: 0,
                  display: 'grid', placeItems: 'center',
                  bgcolor: tokens.color.lineSoft,
                  border: `1px solid ${tokens.color.line}`,
                  fontFamily: tokens.font.mono, fontSize: '0.88rem',
                  color: tokens.color.ash, letterSpacing: '0.02em',
                }}
              >
                {iniziali(valore('nome') || persona.nome)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.5rem',
                    lineHeight: 1.15, color: tokens.color.graphite,
                  }}
                >
                  {valore('nome') || persona.nome}
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.color.ash }}>
                  {[valore('ruolo_testo'), valore('azienda_testo')].filter(Boolean).join(' · ') || '—'}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={onClose} size="small"><Close fontSize="small" /></IconButton>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            {persona.conosciuto_il && (
              <SoftChip label={`conosciuto il ${formatIsoDate(persona.conosciuto_il)}`} tone="info" />
            )}
            <SoftChip
              label={`${persona.conteggio_interazioni ?? 0} interazioni`}
              tone="default"
            />
            {/* Azioni rapide di contatto: il motivo per cui la rubrica esiste */}
            {telefonoPulito && (
              <>
                <Tooltip title="Chiama">
                  <IconButton size="small" component={Link} href={`tel:${telefonoPulito}`}>
                    <Phone fontSize="small" sx={{ color: tokens.color.ash }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="WhatsApp">
                  <IconButton
                    size="small" component={Link} target="_blank" rel="noopener"
                    href={`https://wa.me/${telefonoPulito.replace(/^\+/, '')}`}
                  >
                    <WhatsApp fontSize="small" sx={{ color: tokens.color.ash }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {valore('email') && (
              <Tooltip title="Scrivi">
                <IconButton size="small" component={Link} href={`mailto:${valore('email')}`}>
                  <Email fontSize="small" sx={{ color: tokens.color.ash }} />
                </IconButton>
              </Tooltip>
            )}
            {valore('linkedin') && (
              <Tooltip title="LinkedIn">
                <IconButton
                  size="small" component={Link} target="_blank" rel="noopener"
                  href={valore('linkedin').startsWith('http')
                    ? valore('linkedin')
                    : `https://${valore('linkedin')}`}
                >
                  <LinkedIn fontSize="small" sx={{ color: tokens.color.ash }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* NOTA VOCALE -------------------------------------------------- */}
          <Box sx={{ mb: 3 }}>
            <NotaVocaleCard
              nomePersona={valore('nome') || persona.nome}
              onAnalisi={(dati, daVoce) => setAnalisi({ dati, daVoce })}
              showSnack={showSnack}
            />
          </Box>

          {/* TAG ---------------------------------------------------------- */}
          <Titolo testo="Tag" />
          <Box sx={{ mb: 3 }}>
            {faccette.length === 0 ? (
              <Alert severity="info">
                Tassonomia non ancora configurata: aprila da «Gestisci tag» per creare le faccette.
              </Alert>
            ) : (
              <TagPicker
                faccette={faccette}
                tag={tag}
                selezionati={tagBozza}
                onChange={setTagBozza}
              />
            )}
          </Box>

          {/* CAMPI -------------------------------------------------------- */}
          <Titolo testo="Dati" />
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {CAMPI_TESTO.map(({ campo, label, multi }) => (
              <React.Fragment key={campo}>
                <TextField
                  label={label}
                  value={valore(campo)}
                  onChange={(e) => setBozza((prev) => ({ ...prev, [campo]: e.target.value }))}
                  size="small"
                  fullWidth
                  multiline={multi}
                  minRows={multi ? 2 : undefined}
                />
                {campo === 'ruolo_testo' && (
                  <TelefoniEditor
                    telefoni={telefoni}
                    onChange={(nuovi) => setBozza((prev) => ({ ...prev, telefoni: nuovi }))}
                  />
                )}
              </React.Fragment>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={salva}
              disabled={salvando || !sporca}
              sx={{ textTransform: 'none' }}
            >
              Salva modifiche
            </Button>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Elimina la scheda">
              <IconButton onClick={elimina} disabled={salvando} size="small">
                <Delete fontSize="small" sx={{ color: tokens.color.danger }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* COLLEGAMENTI — la rete: informazione dell'utente, sta PRIMA del web */}
          <Box sx={{ mb: 3 }}>
            <CollegamentiCard
              persona={persona}
              tutte={tutte}
              onCambiato={onSaved}
              onApriPersona={onApriPersona}
              showSnack={showSnack}
            />
          </Box>

          {/* DOSSIER WEB — informazioni di TERZI, sezione dichiarata e separata */}
          <Box sx={{ mb: 3 }}>
            <DossierWebCard
              persona={persona}
              onSalvato={onSaved}
              showSnack={showSnack}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* DIARIO ------------------------------------------------------- */}
          <Titolo testo={`Diario (${interazioni.length})`} />
          {caricandoDiario ? (
            <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          ) : interazioni.length === 0 ? (
            <Typography variant="body2" sx={{ color: tokens.color.mist, fontStyle: 'italic' }}>
              Nessuna interazione registrata. Racconta il primo incontro con una nota vocale.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {interazioni.map((i) => (
                <Paper
                  key={i.id}
                  variant="outlined"
                  sx={{ p: 1.5, borderColor: tokens.color.line, bgcolor: tokens.color.bone }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: tokens.font.mono, fontSize: '0.72rem',
                        color: tokens.color.ash,
                      }}
                    >
                      {formatIsoDate(i.quando)}
                    </Typography>
                    <SoftChip label={CANALE_LABELS[i.canale] ?? i.canale} tone="default" />
                    {i.da_nota_vocale && <SoftChip label="da voce" tone="info" />}
                  </Stack>
                  <Typography variant="body2" sx={{ color: tokens.color.graphite, whiteSpace: 'pre-wrap' }}>
                    {i.sintesi}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      {/* Revisione dell'analisi AI: unico punto da cui si salva una nota */}
      {analisi && (
        <RevisioneAnalisiDialog
          open
          persona={persona}
          analisi={analisi.dati}
          daNotaVocale={analisi.daVoce}
          faccette={faccette}
          tag={tag}
          onClose={() => setAnalisi(null)}
          onConferma={applicaRevisione}
          onTassonomiaCambiata={onTassonomiaCambiata}
          showSnack={showSnack}
        />
      )}
    </>
  );
};

const Titolo: React.FC<{ testo: string }> = ({ testo }) => (
  <Typography
    sx={{
      fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: tokens.color.mist, mb: 1,
    }}
  >
    {testo}
  </Typography>
);
